import { useState } from 'react';
import {
  useOverviewReport,
  useTeamReport,
  useGeneratedReports,
  useGenerateDailyReport,
  useGenerateWeeklyReport,
  useSendReportEmail,
} from '@/hooks/useReports';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  FileText,
  Users,
  GitBranch,
  GitCommit,
  Bot,
  Bell,
  TrendingUp,
  DollarSign,
  BarChart3,
  Calendar,
  Mail,
  CheckCircle,
  XCircle,
  Loader2,
  Plus,
  Eye,
} from 'lucide-react';
import type { GeneratedReport, ReportType } from '@/api/reports.api';

type ReportTab = 'overview' | 'team' | 'generated';
type TimeRange = 7 | 14 | 30 | 90;

export default function Reports() {
  const [activeTab, setActiveTab] = useState<ReportTab>('overview');
  const [timeRange, setTimeRange] = useState<TimeRange>(30);
  const [reportTypeFilter, setReportTypeFilter] = useState<ReportType | 'all'>('all');
  const [selectedReport, setSelectedReport] = useState<GeneratedReport | null>(null);

  // Fetch report data
  const { data: overviewData, isLoading: overviewLoading } = useOverviewReport(timeRange);
  const { data: teamData, isLoading: teamLoading } = useTeamReport(timeRange);
  const { data: generatedData, isLoading: generatedLoading } = useGeneratedReports({
    type: reportTypeFilter === 'all' ? undefined : reportTypeFilter,
    limit: 20,
  });

  // Mutations
  const generateDaily = useGenerateDailyReport();
  const generateWeekly = useGenerateWeeklyReport();
  const sendEmail = useSendReportEmail();

  const tabs: { id: ReportTab; label: string; icon: typeof FileText }[] = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'team', label: 'Team Performance', icon: Users },
    { id: 'generated', label: 'Generated Reports', icon: Calendar },
  ];

  const timeRanges: { value: TimeRange; label: string }[] = [
    { value: 7, label: '7 days' },
    { value: 14, label: '14 days' },
    { value: 30, label: '30 days' },
    { value: 90, label: '90 days' },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3" /> Completed
          </span>
        );
      case 'sent':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            <Mail className="h-3 w-3" /> Sent
          </span>
        );
      case 'generating':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
            <Loader2 className="h-3 w-3 animate-spin" /> Generating
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3" /> Failed
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground">
            System analytics and team performance metrics
          </p>
        </div>
        {activeTab !== 'generated' && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Time range:</span>
            {timeRanges.map((range) => (
              <Button
                key={range.value}
                variant={timeRange === range.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeRange(range.value)}
              >
                {range.label}
              </Button>
            ))}
          </div>
        )}
        {activeTab === 'generated' && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => generateDaily.mutate(undefined)}
              disabled={generateDaily.isPending}
            >
              {generateDaily.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Generate Daily
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => generateWeekly.mutate(undefined)}
              disabled={generateWeekly.isPending}
            >
              {generateWeekly.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Generate Weekly
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2"
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {overviewLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : overviewData ? (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Users
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{overviewData.users.total}</div>
                    <p className="text-xs text-muted-foreground">
                      {overviewData.users.active} active
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <GitBranch className="h-4 w-4" />
                      Repositories
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{overviewData.repositories.total}</div>
                    <p className="text-xs text-muted-foreground">
                      {overviewData.repositories.withWebhooks} with webhooks
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <GitCommit className="h-4 w-4" />
                      Commits
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{overviewData.commits.total}</div>
                    <p className="text-xs text-muted-foreground">
                      {overviewData.commits.uniqueAuthors} contributors
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      AI Costs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ${overviewData.aiCosts?.totalCost?.toFixed(2) || '0.00'}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {overviewData.aiCosts?.requestCount || 0} requests
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Code Changes */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Code Activity
                  </CardTitle>
                  <CardDescription>Lines of code changed in the period</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <p className="text-3xl font-bold text-green-600">
                        +{overviewData.commits.additions.toLocaleString()}
                      </p>
                      <p className="text-sm text-muted-foreground">Lines Added</p>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <p className="text-3xl font-bold text-red-600">
                        -{overviewData.commits.deletions.toLocaleString()}
                      </p>
                      <p className="text-sm text-muted-foreground">Lines Removed</p>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <p className="text-3xl font-bold">
                        {(overviewData.commits.additions - overviewData.commits.deletions).toLocaleString()}
                      </p>
                      <p className="text-sm text-muted-foreground">Net Change</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Reviews */}
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bot className="h-5 w-5" />
                      AI Code Reviews
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Total Reviews</span>
                        <span className="font-bold">{overviewData.reviews.total}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Completed</span>
                        <span className="font-bold text-green-600">
                          {overviewData.reviews.completed}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Average Score</span>
                        <span className="font-bold">{overviewData.reviews.averageScore}/100</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Issues Found</span>
                        <span className="font-bold text-orange-600">
                          {overviewData.reviews.totalIssues}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

              </div>

              {/* Alerts Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5" />
                    Alerts Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-5">
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold">{overviewData.alerts.total}</p>
                      <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-600">
                        {overviewData.alerts.bySeverity?.critical || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Critical</p>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-600">
                        {overviewData.alerts.bySeverity?.high || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">High</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg">
                      <p className="text-2xl font-bold text-yellow-600">
                        {overviewData.alerts.bySeverity?.medium || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Medium</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">
                        {overviewData.alerts.unacknowledged}
                      </p>
                      <p className="text-xs text-muted-foreground">Unacknowledged</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* User Roles */}
              <Card>
                <CardHeader>
                  <CardTitle>Users by Role</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-5">
                    {Object.entries(overviewData.users.byRole).map(([role, count]) => (
                      <div key={role} className="text-center p-3 bg-muted/50 rounded-lg">
                        <p className="text-2xl font-bold">{count}</p>
                        <p className="text-xs text-muted-foreground capitalize">{role}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No data available</h3>
                <p className="text-muted-foreground">
                  Start syncing data to generate reports
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Team Tab */}
      {activeTab === 'team' && (
        <>
          {teamLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : teamData ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Team Size</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{teamData.summary.totalDevelopers}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Total Commits</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{teamData.summary.totalCommits}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Avg Commits/Dev</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{teamData.summary.averageCommitsPerDev}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Avg Review Score</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{teamData.summary.averageReviewScore}/100</div>
                  </CardContent>
                </Card>
              </div>

              {/* Developer Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Developer Performance</CardTitle>
                  <CardDescription>Individual metrics for the selected period</CardDescription>
                </CardHeader>
                <CardContent>
                  {teamData.developers.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      No developer data available
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-2 font-medium">Developer</th>
                            <th className="text-center py-3 px-2 font-medium">Commits</th>
                            <th className="text-center py-3 px-2 font-medium">Lines Added</th>
                            <th className="text-center py-3 px-2 font-medium">Lines Removed</th>
                            <th className="text-center py-3 px-2 font-medium">Review Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamData.developers.map((dev) => (
                            <tr key={dev.user.id} className="border-b hover:bg-muted/50">
                              <td className="py-3 px-2">
                                <div>
                                  <p className="font-medium">{dev.user.displayName}</p>
                                  <p className="text-xs text-muted-foreground">@{dev.user.username}</p>
                                </div>
                              </td>
                              <td className="text-center py-3 px-2 font-medium">
                                {dev.commits.count}
                              </td>
                              <td className="text-center py-3 px-2 text-green-600">
                                +{dev.commits.additions.toLocaleString()}
                              </td>
                              <td className="text-center py-3 px-2 text-red-600">
                                -{dev.commits.deletions.toLocaleString()}
                              </td>
                              <td className="text-center py-3 px-2">
                                {dev.reviews.count > 0 ? (
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                      dev.reviews.averageScore >= 80
                                        ? 'bg-green-100 text-green-800'
                                        : dev.reviews.averageScore >= 60
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}
                                  >
                                    {dev.reviews.averageScore}/100
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No team data available</h3>
                <p className="text-muted-foreground">
                  Team performance data will appear here once developers start working
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Generated Reports Tab */}
      {activeTab === 'generated' && (
        <>
          {/* Filter */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-muted-foreground">Filter by type:</span>
            {(['all', 'daily', 'weekly'] as const).map((type) => (
              <Button
                key={type}
                variant={reportTypeFilter === type ? 'default' : 'outline'}
                size="sm"
                onClick={() => setReportTypeFilter(type)}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Button>
            ))}
          </div>

          {generatedLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : !generatedData?.reports?.length ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No generated reports</h3>
                <p className="text-muted-foreground mb-4">
                  Reports are automatically generated daily and weekly, or you can generate one manually.
                </p>
                <div className="flex justify-center gap-2">
                  <Button
                    onClick={() => generateDaily.mutate(undefined)}
                    disabled={generateDaily.isPending}
                  >
                    {generateDaily.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Generate Daily Report
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => generateWeekly.mutate(undefined)}
                    disabled={generateWeekly.isPending}
                  >
                    {generateWeekly.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Generate Weekly Report
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {/* Report List */}
              <Card className={selectedReport ? '' : 'lg:col-span-2'}>
                <CardHeader>
                  <CardTitle>Generated Reports</CardTitle>
                  <CardDescription>
                    {generatedData.total} report{generatedData.total !== 1 ? 's' : ''} found
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {generatedData.reports.map((report) => (
                      <div
                        key={report._id}
                        className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                          selectedReport?._id === report._id
                            ? 'border-primary bg-primary/5'
                            : 'hover:border-primary/50'
                        }`}
                        onClick={() => setSelectedReport(report)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className="font-medium">{report.title}</h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDate(report.periodStart)} - {formatDate(report.periodEnd)}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {getStatusBadge(report.status)}
                            <span className="text-xs text-muted-foreground capitalize">
                              {report.type}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                          <span>{report.metrics.totalCommits} commits</span>
                          <span>{report.metrics.activeUsers} users</span>
                          <span>Quality: {report.metrics.avgCodeQuality}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Report Detail */}
              {selectedReport && (
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{selectedReport.title}</CardTitle>
                        <CardDescription>
                          {formatDate(selectedReport.periodStart)} - {formatDate(selectedReport.periodEnd)}
                        </CardDescription>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedReport(null)}
                      >
                        Close
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Metrics */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-muted/50 rounded-lg text-center">
                        <p className="text-xl font-bold">{selectedReport.metrics.totalCommits}</p>
                        <p className="text-xs text-muted-foreground">Commits</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg text-center">
                        <p className="text-xl font-bold">{selectedReport.metrics.activeUsers}</p>
                        <p className="text-xs text-muted-foreground">Active Users</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg text-center">
                        <p className="text-xl font-bold">{selectedReport.metrics.avgCodeQuality}</p>
                        <p className="text-xs text-muted-foreground">Avg Quality</p>
                      </div>
                    </div>

                    {/* Highlights */}
                    {selectedReport.summary.highlights.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 text-green-600">Highlights</h4>
                        <ul className="space-y-1">
                          {selectedReport.summary.highlights.map((h, i) => (
                            <li key={i} className="text-sm flex items-start gap-2">
                              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {h}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Concerns */}
                    {selectedReport.summary.concerns.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 text-red-600">Concerns</h4>
                        <ul className="space-y-1">
                          {selectedReport.summary.concerns.map((c, i) => (
                            <li key={i} className="text-sm flex items-start gap-2">
                              <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                              {c}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Recommendations */}
                    {selectedReport.summary.recommendations.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 text-yellow-600">Recommendations</h4>
                        <ul className="space-y-1">
                          {selectedReport.summary.recommendations.map((r, i) => (
                            <li key={i} className="text-sm flex items-start gap-2">
                              <TrendingUp className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                              {r}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendEmail.mutate(selectedReport._id)}
                        disabled={sendEmail.isPending || selectedReport.status === 'sent'}
                      >
                        {sendEmail.isPending ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4 mr-2" />
                        )}
                        {selectedReport.status === 'sent' ? 'Already Sent' : 'Send Email'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`/api/reports/generated/${selectedReport._id}/html`, '_blank')}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View HTML
                      </Button>
                    </div>

                    {/* Delivery Status */}
                    {selectedReport.delivery.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2">Delivery History</h4>
                        <div className="space-y-2">
                          {selectedReport.delivery.map((d, i) => (
                            <div key={i} className="text-xs p-2 bg-muted/50 rounded flex items-center justify-between">
                              <span className="capitalize">{d.method}: {d.recipients.length} recipients</span>
                              {getStatusBadge(d.status)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
